import { useState, useEffect, useCallback } from 'react';

const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_SPOTIFY_CLIENT_SECRET;
const TOKEN_URL = 'https://accounts.spotify.com/api/token';
const SEARCH_URL = 'https://api.spotify.com/v1/search';
const ID_CACHE_KEY = 'bb-spotify-artist-ids';

const tokenCache = {
  value: null,
  expiresAt: 0,
};

const memoryIds = new Map();

const cacheKey = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const readStoredIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ID_CACHE_KEY) || '{}');
  } catch (err) {
    return {};
  }
};

const getCachedId = (name) => {
  const key = cacheKey(name);
  if (!key) return undefined;
  if (memoryIds.has(key)) return memoryIds.get(key);
  const stored = readStoredIds();
  if (Object.prototype.hasOwnProperty.call(stored, key)) {
    memoryIds.set(key, stored[key]);
    return stored[key];
  }
  return undefined;
};

const setCachedId = (name, id) => {
  const key = cacheKey(name);
  if (!key) return;
  memoryIds.set(key, id);
  try {
    const stored = readStoredIds();
    stored[key] = id;
    sessionStorage.setItem(ID_CACHE_KEY, JSON.stringify(stored));
  } catch (err) {
    // Private mode can block sessionStorage; memory cache still helps.
  }
};

const clearCachedId = (name) => {
  const key = cacheKey(name);
  if (!key) return;
  memoryIds.delete(key);
  try {
    const stored = readStoredIds();
    delete stored[key];
    sessionStorage.setItem(ID_CACHE_KEY, JSON.stringify(stored));
  } catch (err) {
    // Ignore storage failures.
  }
};

const getAccessToken = async () => {
  if (tokenCache.value && Date.now() < tokenCache.expiresAt) {
    return tokenCache.value;
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': 'Basic ' + btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
    },
    body: new URLSearchParams({
      grant_type: 'client_credentials',
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch access token');
  }

  tokenCache.value = data.access_token;
  tokenCache.expiresAt = Date.now() + Math.max(30, (data.expires_in || 3600) - 60) * 1000;
  return tokenCache.value;
};

export const useSpotify = (artistName, artistCountry, artistGenres) => {
  const cached = artistName ? getCachedId(artistName) : undefined;
  const [spotifyId, setSpotifyId] = useState(cached || null);
  const [error, setError] = useState(cached === null ? 'Artist not found on Spotify' : null);
  const [isCloseMatch, setIsCloseMatch] = useState(Boolean(cached));
  const [isLoading, setIsLoading] = useState(Boolean(artistName) && cached === undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    clearCachedId(artistName);
    setRefreshKey((value) => value + 1);
  }, [artistName]);

  useEffect(() => {
    let cancelled = false;

    const searchArtist = async () => {
      const hit = getCachedId(artistName);
      if (hit !== undefined) {
        setSpotifyId(hit);
        setIsCloseMatch(Boolean(hit));
        setError(hit ? null : 'Artist not found on Spotify');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessToken();
        if (cancelled) return;

        const response = await fetch(`${SEARCH_URL}?q=${encodeURIComponent(artistName)}&type=artist&limit=5`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        const matches = data.artists?.items || [];
        const needle = cacheKey(artistName);
        const bestMatch = matches.find((artist) => cacheKey(artist.name) === needle);

        if (cancelled) return;

        if (bestMatch) {
          setCachedId(artistName, bestMatch.id);
          setSpotifyId(bestMatch.id);
          setIsCloseMatch(true);
          setError(null);
        } else {
          setCachedId(artistName, null);
          setSpotifyId(null);
          setIsCloseMatch(false);
          setError('Artist not found on Spotify');
        }
      } catch (err) {
        if (!cancelled) {
          setSpotifyId(null);
          setIsCloseMatch(false);
          setError('Failed to fetch Spotify ID');
        }
        console.error(err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    if (artistName) {
      searchArtist();
    } else {
      setSpotifyId(null);
      setError(null);
      setIsCloseMatch(true);
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [artistName, artistCountry, artistGenres, refreshKey]);

  const resolvedId = cached !== undefined ? cached : spotifyId;
  const resolvedMatch = cached !== undefined ? Boolean(cached) : isCloseMatch;
  const resolvedLoading = cached !== undefined ? false : isLoading;

  return {
    spotifyId: resolvedId,
    topTracks: [],
    error: cached === null ? 'Artist not found on Spotify' : error,
    isCloseMatch: resolvedMatch,
    isLoading: resolvedLoading,
    refetch,
  };
};
